import React, { useEffect, useMemo } from 'react';
import styled from "styled-components";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { Trans, useTranslation } from "react-i18next";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import {
  selectActiveOfficeManagementPanel,
  selectFocusedChildOffice,
  setActiveOfficeManagementPanel,
  setFocusedChildOffice
} from '@features/desks/store/editOfficeLayout.duck';
import { OfficeManagementPanelView } from "../data/OfficeManagement.model";
import { useDispatch, useSelector } from "react-redux";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import {
  addDesk,
  EditableDesk,
  editDesk,
  fetchDesks,
  saveEditDeskChanges,
  selectEditingDesks,
  selectIsEditDesksDirty,
  selectIsEditDesksLoading
} from '@features/desks/store/editDesk.duck';
import TextField from '@shared/ui/atoms/TextField';
import { Switch } from '@shared/ui/atoms/Switch';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { PendingSaveDot } from '@shared/ui/atoms/PendingSaveDot';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canUserEditOfficesInManagement } from '@shared/utils/AccessControl';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function DeskManagementPanel(props: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const isEditable = useMemo(() => canUserEditOfficesInManagement(currentUser), [currentUser]);
  const isDirty = useSelector(selectIsEditDesksDirty);
  const isLoading = useSelector(selectIsEditDesksLoading);
  const parentOffice = useSelector(selectFocusedChildOffice);
  const activePanel = useSelector(selectActiveOfficeManagementPanel);
  const desks = useSelector(selectEditingDesks);


  useEffect(() => {
    if (parentOffice && activePanel === OfficeManagementPanelView.DeskManagement) {
      dispatch(fetchDesks(parentOffice.id));
    }
  }, [activePanel, dispatch, parentOffice]);

  const goBackToDeskManagement = () => {
    dispatch(setFocusedChildOffice(undefined));
    dispatch(setActiveOfficeManagementPanel(OfficeManagementPanelView.DeskManagement));
  }

  const updateDeskLabel = (desk: EditableDesk, label: string, active: boolean) => {
    dispatch(editDesk({
      original: desk,
      updated: {
        ...desk, label: label, active: active,
      }
    }));
  }

  const addNewDesk = () => {
    if (!parentOffice) {
      return;
    }

    const newDesk = {
      label: '',
      officeId: parentOffice.id,
      rootOfficeId: parentOffice.parentId,
      id: 0,
      active: true,
      dirty: true,
    } as any;
    dispatch(addDesk(newDesk))
  }

  const saveChanges = async () => {
    if (!parentOffice) {
      return;
    }

    dispatch(saveEditDeskChanges(parentOffice.id));
  }

  const onDeleteClicked = (desk: EditableDesk) => {
    dispatch(openDialogWithPayload({
      payload: { deskId: desk.id, officeId: desk.officeId },
      activeDialog: DialogIdentifiers.DeleteDeskEntityDialog,
    }));
  }

  return (
    <Container>
      <GoBackButton onClick={goBackToDeskManagement} />
      <HeadlineMedium style={{marginBottom: 24}}>{t('textinput.desk-names-and-avail')}</HeadlineMedium>


      {parentOffice?.floorPlanEnabled && <>
        <FlatContentCard style={{marginBottom: 24}}>
          <BodyRegular style={{marginBottom: 8}} weight={600}>{t('settings.manage-desk.floor-plans-enabled')}</BodyRegular>
          <BodyRegular><Trans i18nKey={'settings.manage-desk.contact-us-to-change'}
                              components={[<a href="mailto:hello@team-today.com">hello@team-today.com</a>]} /></BodyRegular>
        </FlatContentCard>
      </>}

      {isLoading ? <>
        <Center><LoadingSpinner hideBorder={true} /></Center>
      </> : <>
        {desks.map((desk: EditableDesk, key: number) => <DeskRow key={key}>
          <BodyRegular weight={600}>{t('settings.manage-desk.desk-name')}</BodyRegular>
          <ManageDeskRow>
            <LabelTextFieldContainer>
              <TextField onChange={(val: string) => updateDeskLabel(desk, val, desk.active)}
                         value={desk.label}
                         disabled={!isEditable}
                         fullWidth={true} />
            </LabelTextFieldContainer>
            <Switch value={desk.active}
                    disabled={!isEditable}
                    label={""}
                    onChange={enabled => updateDeskLabel(desk, desk.label, enabled)} />

            {!!desk.id && <div>
              <IconButton icon={IconTypes.Bin} onClick={() => onDeleteClicked(desk)} />
            </div>}


            <PendingSaveDot visible={desk.dirty ?? false} style={{marginLeft: 16}} />
          </ManageDeskRow>
        </DeskRow>)}
      </>}


      <OutlineButton click={addNewDesk}
                     text={"button.add-new-desk"}
                     size={ButtonSize.Medium}
                     fullWidth={true}
                     style={{marginBottom: 16, marginTop: 32}}
                     disabled={!isEditable || isLoading} />

      <PrimaryButton click={saveChanges}
                     text={"button.save-changes"}
                     fullWidth={true}
                     size={ButtonSize.Large}
                     disabled={(isEditable && !isDirty) || isLoading} />
    </Container>
  )
}

interface Props {
  office: OfficeEntity;
}

const Container = styled.div`
  
`


const DeskRow = styled.div`
  margin-bottom: 24px;
`
const ManageDeskRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`
const LabelTextFieldContainer = styled.div`
  display: flex;
  width: 70%;
  padding-right: 20px;
  .textField {
    margin: 0;
  }
`
